/**
 * The QR code of the card on the net (F22, docs/15-getraenkekarte.md): made in the browser, also
 * offline. Medium error correction, a quiet zone of four modules, no logo in the middle.
 */
import qrcode from "qrcode-generator";

const QUIET = 4;

/** The dark modules of a link, row by row. */
export function qrMatrix(text: string): boolean[][] {
  const qr = qrcode(0, "M");
  qr.addData(text, "Byte");
  qr.make();
  const n = qr.getModuleCount();
  return Array.from({ length: n }, (_, r) => Array.from({ length: n }, (_, c) => qr.isDark(r, c)));
}

/** As SVG, one path, scales to any size (for the printer). */
export function qrSvg(text: string, title = ""): string {
  const m = qrMatrix(text);
  const size = m.length + 2 * QUIET;
  let d = "";
  m.forEach((row, r) => row.forEach((dark, c) => dark && (d += `M${c + QUIET} ${r + QUIET}h1v1h-1z`)));
  const t = title.replace(/&/g, "&amp;").replace(/</g, "&lt;");
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges">${t ? `<title>${t}</title>` : ""}<rect width="${size}" height="${size}" fill="#fff"/><path d="${d}" fill="#000"/></svg>`;
}

/** As PNG of about `px` pixels: whole pixels per module, so the edges stay sharp. */
export function qrPng(text: string, px: number): Promise<Blob> {
  const m = qrMatrix(text);
  const modules = m.length + 2 * QUIET;
  const scale = Math.max(1, Math.round(px / modules));
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = modules * scale;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#000";
  m.forEach((row, r) => row.forEach((dark, c) => dark && ctx.fillRect((c + QUIET) * scale, (r + QUIET) * scale, scale, scale)));
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("PNG ließ sich nicht erzeugen"))), "image/png"));
}

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** An A4 sheet with eight stickers (two columns, four rows) for the tables, with lines to cut along. */
export function stickerSheetHtml(url: string, house: string, name = "Getränkekarte"): string {
  const svg = qrSvg(url);
  const shown = url.replace(/^https?:\/\//, "");
  const sticker = `<div class="s"><div class="qr">${svg}</div><div class="t"><div class="h">${esc(house)}</div><div class="n">${esc(name)}</div><div class="c">Mit der Handykamera scannen</div></div><div class="u">${esc(shown)}</div></div>`;
  return `<!doctype html><html lang="de"><meta charset="utf-8"><title>Aufkleber ${esc(house)}</title><style>
/* Safari on the iPhone keeps its own margins and a line of header and footer: the sheet stays inside them. */
@page { size: A4; margin: 12mm; }
* { box-sizing: border-box; }
body { margin: 0; font-family: "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif; color: #2a2522; }
.sheet { width: 186mm; height: 268mm; display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: repeat(4, 1fr); }
.s { display: grid; grid-template-columns: 40mm minmax(0, 1fr); align-content: center; column-gap: 5mm; row-gap: 2mm; padding: 4mm 5mm; border: 0.2mm dashed #c9c0b8; overflow: hidden; }
.qr { width: 40mm; height: 40mm; }
.qr svg { width: 100%; height: 100%; display: block; }
.t { min-width: 0; }
/* Long names break between words, a single long word by syllables, never in the middle at random. */
.h { font-size: 14pt; font-variant: small-caps; letter-spacing: 0.03em; line-height: 1.15; hyphens: auto; }
.n { font-size: 12pt; color: #7b2d3a; margin-top: 1.5mm; }
.c { font: 8.5pt system-ui, sans-serif; color: #5a4f48; margin-top: 3mm; }
/* The link on its own line under both, never broken. */
.u { grid-column: 1 / -1; font: 7.5pt ui-monospace, monospace; color: #8a7d74; white-space: nowrap; text-align: center; }
</style><body><div class="sheet">${sticker.repeat(8)}</div></body></html>`;
}
